import { AdminDefaultHsaStoreInfo, FetchState, HsaStoreVisibility, HsaStoreVisibilityRequest } from "@/shared/types";
import { getDefaultHsaStoreConfigs, setDefaultHsaStoreConfigs, getHsaStoreVisibilityForPartner,
  setHsaStoreVisiblitiesForPartner, getHsaStoreVisibilityForEmployer,
  setHsaStoreVisiblitiesForEmployer } from "@/shared/api/userService";
import { defineStore } from "pinia";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";
import { AdminHsaStoreInfoStore } from "../types";
import * as storeHelper from "@/shared/storeHelper";

export const initialState: AdminHsaStoreInfoStore = {
  defaultHsaStoreInfoFetchState: FetchState.Uninitialized,
  hsaStoreInfoFetchState: FetchState.Uninitialized,
  partnerHsaStoreInfoFetchState: FetchState.Uninitialized,
  employerHsaStoreInfoFetchState: FetchState.Uninitialized,
  defaultHsaStoreInfos: [],
  hsaStoreInfos: [],
  partnerHsaStoreInfos: [],
  employerHsaStoreInfos: [],
};

export const useAdminHsaStoreInfoStore = defineStore("adminHsaStoreInfo", {
  state: () => (_.cloneDeep(initialState)),
  actions: {
    loadDefaultStoreInfos (): Promise<void> {
      this.defaultHsaStoreInfoFetchState = FetchState.Loading;
      this.defaultHsaStoreInfos = [];

      return getDefaultHsaStoreConfigs()
        .then(response => {
          this.defaultHsaStoreInfos = response.data;
          this.defaultHsaStoreInfoFetchState = FetchState.Loaded;
        })
        .catch(() => {
          this.defaultHsaStoreInfoFetchState = FetchState.Uninitialized;

          const notifier = useNotifierStore();
          notifier.setDanger("Unable to load default HSA stores");
        });
    },
    getDefaultHsaStoreInfos (): AdminDefaultHsaStoreInfo[] {
      return this.defaultHsaStoreInfos;
    },
    getDefaultHsaStoreInfoById (id: number): AdminDefaultHsaStoreInfo {
        return this.defaultHsaStoreInfos.find(x => x.id === id);
    },
    unloadDefaultHsaStoreInfos () {
      this.defaultHsaStoreInfos = [];
      this.defaultHsaStoreInfoFetchState = FetchState.Uninitialized;
    },
    saveDefaultHsaStoreInfos (requests: AdminDefaultHsaStoreInfo[]) {
      return setDefaultHsaStoreConfigs(requests)
        .then(() => {
          const notifierStore = useNotifierStore();
          notifierStore.setSuccess("Saved HSA store info");
        })
        .catch(err => {
          storeHelper.handleError({ error: err, rethrow: true });
          const notifier = useNotifierStore();
          notifier.setDanger("Unable to save HSA store info");
        });
    },
    isDefaultHsaStoreInfosLoaded (): boolean {
      return this.defaultHsaStoreInfoFetchState === FetchState.Loaded;
    },
    getPartnerHsaStoreInfos (partnerId: number): HsaStoreVisibility[] {
      if (this.partnerHsaStoreInfoFetchState === FetchState.Loaded) {
        return this.partnerHsaStoreInfos;
      }

      this.partnerHsaStoreInfoFetchState = FetchState.Loading;
      this.partnerHsaStoreInfos = [];

      getHsaStoreVisibilityForPartner (partnerId)
        .then(response => {
          this.partnerHsaStoreInfos = response.data;
          this.partnerHsaStoreInfoFetchState = FetchState.Loaded;
        })
        .catch(() => {
          this.partnerHsaStoreInfoFetchState = FetchState.Uninitialized;
          const notifier = useNotifierStore();
          notifier.setDanger("Unable to load partner hsa stores");
        });
        return this.partnerHsaStoreInfos;
    },
    unloadPartnerHsaStoreInfos () {
      this.partnerHsaStoreInfos = [];
      this.partnerHsaStoreInfoFetchState = FetchState.Uninitialized;
    },
    savePartnerHsaStoreInfos (partnerId: number, requests: HsaStoreVisibilityRequest[]) {
      return setHsaStoreVisiblitiesForPartner(partnerId, requests).then(() => {
        const notifierStore = useNotifierStore();
        notifierStore.setSuccess("Partner Hsa Store Info Successfully Saved");
      }).catch(err => {
          storeHelper.handleError({ error: err, rethrow: true });
        });
    },
    getEmployerHsaStoreInfos (employerId: number): HsaStoreVisibility[] {
      if (
        this.employerHsaStoreInfoFetchState === FetchState.Loaded ||
        this.employerHsaStoreInfoFetchState === FetchState.Loading
      ) {
        return this.employerHsaStoreInfos;
      }

      this.employerHsaStoreInfoFetchState = FetchState.Loading;

      getHsaStoreVisibilityForEmployer(employerId)
        .then(response => {
          this.employerHsaStoreInfos = response.data;
          this.employerHsaStoreInfoFetchState = FetchState.Loaded;
        })
        .catch(() => {
          this.employerHsaStoreInfoFetchState = FetchState.Uninitialized;
          const notifier = useNotifierStore();
          notifier.setDanger("Unable to load employer hsa stores");
        });

      return this.employerHsaStoreInfos;
    },
    unloadEmployerHsaStoreInfos () {
        this.employerHsaStoreInfos = [];
        this.employerHsaStoreInfoFetchState = FetchState.Uninitialized;
    },
    saveEmployerHsaStoreInfos (employerId: number, requests: HsaStoreVisibilityRequest[]) {
      return setHsaStoreVisiblitiesForEmployer(employerId, requests)
        .then(() => {
          const notifierStore = useNotifierStore();
          notifierStore.setSuccess("Employer Hsa Store Info Successfully Saved");
        })
        .catch(err => {
          storeHelper.handleError({ error: err, rethrow: true });
        });
    }
  }
});